import React, { useState, useEffect } from 'react';
import { Key, Settings, ArrowRight, MessageSquare } from 'lucide-react';
import { SettingsPanel } from '@/components/layout/settings/SettingsPanel';
import { getPlatformDisplayName } from '@/utils/platformDisplay';

const EmptyState = ({ type = 'loading', currentConversation, platformNeeded }) => {
  const [isSettingsPanelOpen, setIsSettingsPanelOpen] = useState(false);
  const [initialTab, setInitialTab] = useState('profile');
  const platformNeededLabel = getPlatformDisplayName(platformNeeded);
  const currentConversationPlatformLabel = getPlatformDisplayName(currentConversation?.botPlatform);

  const handleSettingsClick = () => {
    setInitialTab('profile');
    setIsSettingsPanelOpen(true);
  };

  const handleKeysClick = () => {
    setInitialTab('api_keys');
    setIsSettingsPanelOpen(true);
  };

  // Determine if we should show bot details (non keys empty state)
  const showBotDetails =
    currentConversation?.botName &&
    currentConversation?.botPlatform &&
    currentConversation?.botCleanModelName;

  // If type is loading, render nothing
  if (type === 'loading') {
    return <div className="flex-1" />;
  }

  return (
    <div className="flex-1 flex items-center justify-center p-4">
      <div className="max-w-sm w-full space-y-5">
        {type === 'keys' ? (
          <div className="space-y-5">
            <div className="flex justify-center">
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-primary to-violet-500 rounded-full blur-md animate-pulse" />
                <div className="relative bg-surface-dark p-3 rounded-full">
                  <Key className="w-8 h-8 text-primary animate-bounce" />
                </div>
              </div>
            </div>

            <div className="text-center space-y-3">
              <h2 className="text-lg font-semibold text-on-surface">
                {platformNeeded
                  ? `${platformNeededLabel} API Key Required`
                  : 'Add Your First API Key'}
              </h2>
              <p className="text-sm text-on-surface-dim">
                {platformNeeded
                  ? `To chat with ${currentConversation?.botName || 'this bot'}, you'll need to add your ${platformNeededLabel} API key 🔑`
                  : 'Add at least one API key to fire things up and start chatting 🎉'}
              </p>
              <div className="flex flex-col gap-2 pt-3">
                <button
                  onClick={handleKeysClick}
                  className="flex items-center justify-between px-3 py-2 bg-primary 
                             hover:bg-primary-dark rounded-md text-white transition-all
                             duration-200 group text-sm"
                >
                  <span className="font-medium">
                    {platformNeeded ? `Add ${platformNeededLabel} Key` : 'Add Your Keys'}
                  </span>
                  <ArrowRight className="w-4 h-4 transform group-hover:translate-x-1 transition-transform" />
                </button>

                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-md blur-sm" />
                  <button
                    onClick={handleSettingsClick}
                    className="relative w-full flex items-center justify-between px-3 py-2
                               bg-surface-dark hover:bg-surface rounded-md 
                               text-on-surface-dim transition-all duration-200 text-sm"
                  >
                    <span className="font-medium">Settings</span>
                    <Settings className="w-4 h-4" />
                  </button>
                </div>
              </div>

              <p className="text-xs text-on-surface-dim pt-4">
                {platformNeeded
                  ? 'You can manage your API keys anytime in settings ⚙️'
                  : 'You can always access these options later through the menu 🚀'}
              </p>
            </div>
          </div>
        ) : showBotDetails ? (
          <div className="space-y-5 transition-opacity duration-150 opacity-100">
            <div className="flex justify-center">
              <div className="relative w-14 h-14 flex items-center justify-center">
                <div className="absolute inset-0 rounded-full">
                  <div
                    className="absolute w-full h-full rounded-full bg-primary animate-pulseGlow"
                    style={{
                      transform: 'scale(1.05)',
                      filter: 'blur(2px)',
                    }}
                  />
                </div>
                <div className="relative z-10 bg-surface-dark p-3 rounded-full">
                  <MessageSquare className="w-8 h-8 text-primary" />
                </div>
              </div>
            </div>

            <div className="text-center space-y-3">
              <div className="space-y-1">
                <h2 className="text-lg font-semibold text-on-surface">
                  {currentConversation.botName}
                </h2>
                <div className="space-y-1">
                  <p className="text-sm text-primary-light">
                    {currentConversationPlatformLabel} · {currentConversation.botCleanModelName}
                  </p>
                </div>
              </div>
              <p className="text-sm text-on-surface-dim">
                Start a conversation by sending a message below.
              </p>
              <div className="mt-6 text-xs text-on-surface-dim">
                <p className="font-medium mb-2">Getting Started:</p>
                <ul className="space-y-2">
                  <li>• Type your message in the input box below</li>
                  <li>• Press Enter to send or use the arrow button</li>
                  <li>• Your conversation will be saved automatically</li>
                  <li>• Click the Tools button to enable/disable tools or switch bots.</li>
                </ul>
              </div>
            </div>
          </div>
        ) : null}
      </div>

      <SettingsPanel
        isOpen={isSettingsPanelOpen}
        onClose={() => setIsSettingsPanelOpen(false)}
        initialTab={initialTab}
      />
    </div>
  );
};

// Wrap the component in React.memo to prevent unnecessary re-renders if props remain constant.
export default React.memo(EmptyState);